import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Url, URLS } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiContextProvidedValue,
  type EditWithMuroBiEvent,
} from '@wix/da-bi/pkg/events';
import {
  ClickDownloadButtonBiEvent,
  BiData,
  MoreMenuClickBiEvent,
} from '@wix/da-bi/pkg/events';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import {
  getStashFolder,
  getStashNext,
  getStashPrev,
} from '../../../../DeviationPage/redux/selectors';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useDeleteDeviationFlow } from '../../../actionHooks/useDeleteDeviationFlow';
import { useFreeDownloadAction } from '../../../buttons/DownloadButton/hooks';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { StashElementPlacementContext } from '../../../ElementPlacementContext/variants/stash/context';
import { useElementPlacementForArea } from '../../../ElementPlacementContext';
import { StashDeviationElementPlacement } from '../../../ElementPlacementContext/variants/stash/types';
import { useCopyLinkAction } from '../CopyLinkButton';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import { usePushModal } from '../../../../utils/hooks/usePushModal';
import { ModalType } from '../../../../Modals/redux/types';

function useDeleteStashDeviationFlow(deviation: PapiDeviation) {
  let next = useSelector(getStashNext);
  let prev = useSelector(getStashPrev);
  next = next ? Url.stashItemLink(next) : undefined;
  prev = prev ? Url.stashItemLink(prev) : undefined;

  const folder = useSelector(getStashFolder);
  let toFolder: string | undefined = undefined;
  if (folder?.hasSubfolders) {
    toFolder = Url.stashFolderLink(folder.folderId);
  }

  return useDeleteDeviationFlow(deviation, {
    redirectTo: next ?? prev ?? toFolder ?? URLS.STASH,
    supportTierDeletion: false,
  });
}

export interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const MoreButton: React.FC<Props> = ({ deviation, className }) => {
  const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
  const isMobile = useContext(MobileContext);
  const placementMapForMenu = useElementPlacementForArea(
    StashDeviationElementPlacement.PrimaryBarMenu,
    StashElementPlacementContext
  );
  const { t } = useTranslation();
  const pushMuroModal = usePushModal(ModalType.MINI_MURO);
  const pushMuroModalMobile = usePushModal(ModalType.MINI_MOBILE_MURO);
  const {
    enactFlow: enactDeleteFlow,
    biData: deleteFlowBiData,
    supportingComponents: deleteFlowSupportingComponents,
  } = useDeleteStashDeviationFlow(deviation);
  const freeDownloadAction = useFreeDownloadAction(deviation, isAuthor);
  const [onCopyLinkClick, copyLinkBiData] = useCopyLinkAction(deviation);

  const { MiniComponent, MobileComponent } = useContext(MuroDepContext);

  return (
    <>
      <IconButtonWithContextMenu
        size="medium"
        icon={EllipsisIcon}
        placement="top-start"
        tooltipText={!isMobile && t('actionbar.more.tooltip')}
        aria-label={t('actionbar.more')}
        className={className}
        biData={BiData<MoreMenuClickBiEvent>({
          evid: 417,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: 'stash-deviation',
        })}
        items={[
          placementMapForMenu.deleteDeviation && {
            label: t('actionbar.delete'),
            biData: deleteFlowBiData,
            onClick: enactDeleteFlow,
          },
          placementMapForMenu.copyLink && {
            label: t('actionbar.stash.copyLink.button.label'),
            biData: copyLinkBiData,
            onClick: onCopyLinkClick,
          },
          placementMapForMenu.freeDownload &&
            freeDownloadAction.hasAction && {
              label: t('actionbar.download'),
              link: freeDownloadAction.href,
              onClick: freeDownloadAction.onClick,
              biData: BiData<ClickDownloadButtonBiEvent>({
                evid: 350,
                ispaiddownload: 0,
                deviationid: deviation.deviationId,
                sectionname: 'action_bar',
              }),
            },
          placementMapForMenu.muroOpen &&
            !isMobile && {
              label: t('actionbar.draw.edit'),
              biData: BiData<EditWithMuroBiEvent>({
                evid: 926,
                deviationid: deviation.deviationId,
                sectionname: BiContextProvidedValue,
                page_itemid: BiContextProvidedValue,
                page_typeid: BiContextProvidedValue,
              }),
              onClick: () => {
                if (MiniComponent) {
                  pushMuroModal({
                    miniComponent: MiniComponent,
                    deviation: deviation,
                  });
                }
              },
            },
          placementMapForMenu.muroOpen &&
            isMobile && {
              label: t('actionbar.draw.edit'),
              biData: BiData<EditWithMuroBiEvent>({
                evid: 926,
                deviationid: deviation.deviationId,
                sectionname: BiContextProvidedValue,
                page_itemid: BiContextProvidedValue,
                page_typeid: BiContextProvidedValue,
              }),
              onClick: () => {
                if (MobileComponent) {
                  pushMuroModalMobile({
                    mobileComponent: MobileComponent,
                    deviation: deviation,
                  });
                }
              },
            },
          placementMapForMenu.createVideo && {
            label: t('actionbar.create.video'),
            link: Url.videoCreatorLink({
              deviation: deviation,
            }),
          },
        ]}
      />
      {deleteFlowSupportingComponents}
    </>
  );
};

MoreButton.displayName = 'MoreButton';

export default MoreButton;
